import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { capstoneService } from '../../services'
import {
  RocketLaunchIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  TrophyIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid, TrophyIcon as TrophySolid } from '@heroicons/react/24/solid'

export default function CapstoneList() {
  const [capstones, setCapstones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchCapstones()
  }, [])

  const fetchCapstones = async () => {
    try {
      setLoading(true)
      const data = await capstoneService.getAll()
      setCapstones(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load capstones')
    } finally {
      setLoading(false)
    }
  }

  const getDifficultyColor = (capstone) => {
    const hours = capstone.estimated_hours
    if (hours <= 8) return 'text-green-600 bg-green-50 border-green-200'
    if (hours <= 12) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  const getDifficultyText = (capstone) => {
    const hours = capstone.estimated_hours
    if (hours <= 8) return 'Intermediate'
    if (hours <= 12) return 'Advanced'
    return 'Expert'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading capstones...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="text-red-800 font-semibold">Error loading capstones</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  const completedCount = capstones.filter(c => c.progress?.status === 'completed').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white">
        <div className="flex items-center mb-4">
          <TrophySolid className="h-10 w-10 mr-3" />
          <h1 className="text-4xl font-bold">Capstone Projects</h1>
        </div>
        <p className="text-green-100 text-lg max-w-3xl mb-6">
          Showcase your skills with real-world AI agent projects. Build production-ready applications that demonstrate your expertise.
        </p>
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <RocketLaunchIcon className="h-5 w-5 mr-2" />
            <span className="text-green-100">{capstones.length} Projects Available</span>
          </div>
          <div className="flex items-center">
            <CheckCircleSolid className="h-5 w-5 mr-2" />
            <span className="text-green-100">{completedCount} Completed</span>
          </div>
          {completedCount === capstones.length && capstones.length > 0 && (
            <div className="flex items-center bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full font-semibold">
              <TrophySolid className="h-5 w-5 mr-2" />
              All Complete!
            </div>
          )}
        </div>
      </div>

      {/* Capstones Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {capstones.map((capstone) => {
          const isCompleted = capstone.progress?.status === 'completed'
          const isInProgress = capstone.progress?.status === 'in_progress'

          return (
            <Link
              key={capstone.id}
              to={`/capstones/${capstone.id}`}
              className="group bg-white rounded-xl border-2 border-gray-200 hover:border-green-400 hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              {/* Project Badge */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 border-b border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <div className="bg-green-500 rounded-lg p-3 group-hover:bg-green-600 transition-colors">
                      <RocketLaunchIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-3">
                      <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                        {capstone.code}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-green-600 transition-colors">
                        {capstone.title}
                      </h3>
                    </div>
                  </div>
                  {isCompleted && (
                    <CheckCircleSolid className="h-8 w-8 text-green-500 flex-shrink-0" />
                  )}
                </div>
              </div>

              <div className="p-6">
                {/* Description */}
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {capstone.description}
                </p>

                {/* Meta Info */}
                <div className="flex items-center flex-wrap gap-3 mb-4">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getDifficultyColor(capstone)}`}>
                    {getDifficultyText(capstone)}
                  </span>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {capstone.estimated_hours} hours
                  </div>
                </div>

                {/* Progress Bar */}
                {isInProgress && capstone.progress?.completion_percentage > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>In Progress</span>
                      <span>{Math.round(capstone.progress.completion_percentage)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill bg-green-500" 
                        style={{ width: `${capstone.progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Skills Covered */}
                {capstone.skills_covered && capstone.skills_covered.source_files && capstone.skills_covered.source_files.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2 flex items-center">
                      <CodeBracketIcon className="h-4 w-4 mr-1" />
                      Source Files:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {capstone.skills_covered.source_files.slice(0, 4).map((skill, idx) => (
                        <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm font-semibold text-green-600 group-hover:text-green-700 flex items-center">
                    {isCompleted ? (
                      <>
                        <TrophyIcon className="h-5 w-5 mr-1" />
                        View Project
                      </>
                    ) : isInProgress ? (
                      <>
                        <PlayCircleIcon className="h-5 w-5 mr-1" />
                        Continue Project
                      </>
                    ) : (
                      <>
                        <RocketLaunchIcon className="h-5 w-5 mr-1" />
                        Start Project
                      </>
                    )}
                  </span>
                  <svg 
                    className="w-5 h-5 text-green-600 transform group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {capstones.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <RocketLaunchIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Capstones Available</h3>
          <p className="text-gray-500">Check back soon for new capstone projects!</p>
        </div>
      )}

      {/* Portfolio Message */}
      {capstones.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
          <div className="flex items-start">
            <TrophyIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Build Your Portfolio
              </h3>
              <p className="text-gray-700">
                Complete these capstone projects to build a strong portfolio of AI agent applications. 
                Each project is designed to showcase different skills and can be shared with potential employers.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
